// =============================================================================
// MeuDriver — Chat "Tirar dúvida" (antes de agendar)
// Conversa aluno ↔ instrutor sem aula (tabela inquiry_messages, 2026-10-12).
// Usado no perfil do instrutor (aluno pergunta) e em Mensagens (os dois lados).
// =============================================================================

import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Send, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export interface InquiryMessage {
  id: string
  student_id: string
  instructor_id: string
  sender_id: string
  content: string
  read_at: string | null
  created_at: string
}

export function inquiryKey(studentId: string, instructorId: string) {
  return ['inquiry', studentId, instructorId] as const
}

export function InquiryChat({
  studentId,
  instructorId,
  otherName,
  className,
}: {
  studentId: string
  instructorId: string
  otherName: string
  className?: string
}) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  const { data: messages = [], isLoading } = useQuery<InquiryMessage[]>({
    queryKey: inquiryKey(studentId, instructorId),
    // Tempo real pode demorar a assinar (ou cair); o polling curto garante a resposta.
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inquiry_messages')
        .select('*')
        .eq('student_id', studentId)
        .eq('instructor_id', instructorId)
        .order('created_at', { ascending: true })
        .limit(300)
      if (error) throw error
      return (data ?? []) as InquiryMessage[]
    },
  })

  // Tempo real + marca como lidas as recebidas.
  useEffect(() => {
    const channel = supabase
      .channel(`inquiry-${studentId}-${instructorId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'inquiry_messages', filter: `student_id=eq.${studentId}` },
        (payload) => {
          const m = payload.new as InquiryMessage
          if (m.instructor_id !== instructorId) return
          qc.setQueryData<InquiryMessage[]>(inquiryKey(studentId, instructorId), (old = []) =>
            old.some((x) => x.id === m.id) ? old : [...old, m])
        })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [studentId, instructorId, qc])

  const unreadFromOther = messages.some((m) => m.sender_id !== user?.id && !m.read_at)
  useEffect(() => {
    if (!unreadFromOther) return
    supabase.rpc('mark_inquiry_read', { p_student: studentId, p_instructor: instructorId })
      .then(() => qc.invalidateQueries({ queryKey: ['inquiry-threads'] }))
  }, [unreadFromOther, studentId, instructorId, qc])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages.length])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    const content = text.trim()
    if (!content || !user) return
    setSending(true)
    const { data, error } = await supabase
      .from('inquiry_messages')
      .insert({ student_id: studentId, instructor_id: instructorId, sender_id: user.id, content })
      .select('*')
      .single()
    setSending(false)
    if (error) {
      toast.error(error.message.includes('Muitas') || error.message.includes('Instrutor') ? error.message : 'Não foi possível enviar.')
      return
    }
    setText('')
    qc.setQueryData<InquiryMessage[]>(inquiryKey(studentId, instructorId), (old = []) =>
      old.some((x) => x.id === (data as InquiryMessage).id) ? old : [...old, data as InquiryMessage])
    qc.invalidateQueries({ queryKey: ['inquiry-threads'] })
  }

  return (
    <div className={cn('flex h-[420px] flex-col', className)}>
      <div className="flex-1 space-y-2 overflow-y-auto pr-1">
        {isLoading ? (
          <div className="space-y-2"><Skeleton className="h-8 w-3/4" /><Skeleton className="ml-auto h-8 w-1/2" /></div>
        ) : messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Pergunte o que quiser a {otherName}: horários, carro, local de encontro…
          </p>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.id
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={cn('max-w-[78%] whitespace-pre-wrap rounded-xl px-3 py-2 text-sm',
                  mine ? 'rounded-br-sm bg-brand-600 text-white' : 'rounded-bl-sm bg-gray-100 text-gray-800')}>
                  {m.content}
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>
      <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
        <ShieldCheck className="h-3 w-3" /> Para sua segurança, agende e pague pela plataforma.
      </p>
      <form onSubmit={send} className="mt-2 flex gap-2 border-t pt-3">
        <input
          className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          placeholder="Escreva sua mensagem..."
          value={text}
          maxLength={1000}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" size="icon" className="h-9 w-9" disabled={sending || !text.trim()} aria-label="Enviar">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  )
}
